/**
 * A field's refusal, under the field — the one way every form says it.
 *
 * It is the field's description while it shows: the control points at it with
 * `aria-describedby={describedBy(hintId, error && errorId)}` and carries
 * `aria-invalid`, so a screen reader reads the refusal with the field, not
 * only when it appears. `id` is required for that reason.
 *
 * Presentation only: the text is the caller's, translated (no i18n here).
 */
import { AlertCircle } from 'lucide-react';
import type { ReactNode } from 'react';

import { cn } from '../lib/utils';

export interface FieldErrorProps {
  id: string;
  /** The refusal; nothing renders without one. */
  children?: ReactNode;
  className?: string;
  'data-testid'?: string;
}

export function FieldError({ id, children, className, 'data-testid': testId }: FieldErrorProps) {
  if (!children) return null;
  return (
    <p
      id={id}
      role="alert"
      data-slot="field-error"
      data-testid={testId}
      className={cn('flex items-start gap-1.5 text-sm text-destructive-accessible', className)}
    >
      <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span>{children}</span>
    </p>
  );
}

/** The ids a control is described by — the ones that are there, or none. */
export function describedBy(...ids: ReadonlyArray<string | false | null | undefined>) {
  const present = ids.filter((id): id is string => typeof id === 'string' && id.length > 0);
  return present.length > 0 ? present.join(' ') : undefined;
}
